import React, { useState, useEffect } from 'react';
import { Network, Zap, CheckCircle, Clock, RefreshCw, AlertCircle, ArrowDown, ArrowRight, Target, Cpu, Shield } from 'lucide-react';

const PenetrationWorkflowDiagram = ({ sessionId }) => {
  const [workflowData, setWorkflowData] = useState({
    common: {
      title: "Common",
      tasks: []
    },
    targets: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorkflowData();

    // Refresh every 10 seconds
    const interval = setInterval(fetchWorkflowData, 10000);
    return () => clearInterval(interval);
  }, [sessionId]);

  const fetchWorkflowData = async () => {
    try {
      const backendUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${backendUrl}/api/v1/exploit/workflow`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure data has required structure to prevent runtime errors
        setWorkflowData({
          common: {
            title: data?.common?.title || "Common",
            tasks: data?.common?.tasks || []
          },
          targets: Array.isArray(data?.targets) ? data.targets.map(t => ({
            ...t,
            tasks: Array.isArray(t?.tasks) ? t.tasks : []
          })) : []
        });
      }
    } catch (error) {
      console.error('Failed to fetch workflow data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      SUCCESS: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/50', icon: CheckCircle },
      RUNNING: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/50', icon: Clock },
      FAILED: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/50', icon: AlertCircle }
    };
    return styles[status] || styles.RUNNING;
  };

  const getStatusIcon = (status) => {
    const StatusIcon = getStatusBadge(status).icon;
    return <StatusIcon className={`w-4 h-4 ${getStatusBadge(status).text}`} />;
  };

  const getStatusText = (status) => {
    const labels = {
      SUCCESS: 'Done',
      RUNNING: 'En cours',
      FAILED: 'Failed'
    };
    return labels[status] || status;
  };

  // Safe access to nested properties with fallbacks to prevent runtime errors
  const commonTasks = workflowData?.common?.tasks || [];
  const targets = workflowData?.targets || [];

  const totalTasks = commonTasks.length + targets.reduce((sum, t) => sum + (t?.tasks?.length || 0), 0);
  const completedTasks = commonTasks.filter(t => t?.status === 'SUCCESS').length +
    targets.reduce((sum, t) => sum + (t?.tasks?.filter(task => task?.status === 'SUCCESS')?.length || 0), 0);
  const runningTasks = commonTasks.filter(t => t?.status === 'RUNNING').length +
    targets.reduce((sum, t) => sum + (t?.tasks?.filter(task => task?.status === 'RUNNING')?.length || 0), 0);

  return (
    <div className="bg-brand-black rounded-xl border border-brand-green/30 p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-brand-green/20 flex items-center justify-center">
            <Network className="w-6 h-6 text-brand-green" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Penetration Workflow</h3>
            <p className="text-sm text-gray-400">Orchestration IA des outils de scan</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {totalTasks > 0 && (
            <div className="flex items-center gap-4 px-4 py-2 bg-brand-black-light rounded-lg border border-gray-700">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-sm text-gray-300">{completedTasks}</span>
              </div>
              {runningTasks > 0 && (
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-400 animate-pulse" />
                  <span className="text-sm text-gray-300">{runningTasks}</span>
                </div>
              )}
              <div className="text-sm text-gray-500">/ {totalTasks} tasks</div>
            </div>
          )}
          <button
            onClick={fetchWorkflowData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green border border-brand-green/50 rounded-lg hover:bg-brand-green/30 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <RefreshCw className="w-10 h-10 text-brand-green animate-spin mb-4" />
          <span className="text-gray-400">Chargement du workflow...</span>
        </div>
      ) : commonTasks.length === 0 && targets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-gray-800 flex items-center justify-center mb-6">
            <Network className="w-10 h-10 text-gray-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-300 mb-2">Aucun workflow actif</h3>
          <p className="text-gray-500 max-w-md">
            Launch a scan to visualize the penetration workflow with AI orchestration
          </p>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          {/* Common Section */}
          {commonTasks.length > 0 && (
            <div className="w-full max-w-2xl mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Cpu className="w-5 h-5 text-blue-400" />
                <h4 className="text-lg font-semibold text-blue-300">Phase Commune</h4>
              </div>
              <div className="bg-gradient-to-br from-blue-900/20 to-blue-800/10 rounded-xl border border-blue-500/30 p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {commonTasks.map((task, idx) => (
                    <div
                      key={idx}
                      className={`bg-brand-black rounded-lg border p-4 transition-all ${
                        task.status === 'RUNNING' ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/10' :
                        task.status === 'SUCCESS' ? 'border-green-500/30' : 'border-red-500/30'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-semibold border border-blue-500/30">
                          {task.toolName}
                        </span>
                        {getStatusIcon(task.status)}
                      </div>
                      <div className="text-xs text-gray-400 space-y-1">
                        <div className="flex justify-between">
                          <span>Debut:</span>
                          <span className="text-gray-300">{task.startedAt}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Fin:</span>
                          <span className="text-gray-300">{task.finishedAt || '-'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Connection Arrow */}
          {commonTasks.length > 0 && targets.length > 0 && (
            <div className="flex flex-col items-center mb-8">
              <div className="w-0.5 h-8 bg-gradient-to-b from-blue-500 to-brand-green"></div>
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-brand-green/20 border-2 border-brand-green flex items-center justify-center">
                  <Zap className="w-5 h-5 text-brand-green" />
                </div>
                <div className="absolute inset-0 w-10 h-10 rounded-full bg-brand-green/20 animate-ping"></div>
              </div>
              <div className="w-0.5 h-8 bg-gradient-to-b from-brand-green to-purple-500"></div>
              <span className="text-xs text-gray-500 mt-2">Decision IA</span>
            </div>
          )}

          {/* Targets Section */}
          {targets.length > 0 && (
            <div className="w-full">
              <div className="flex items-center justify-center gap-2 mb-6">
                <Target className="w-5 h-5 text-purple-400" />
                <h4 className="text-lg font-semibold text-purple-300">Cibles Detectees</h4>
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full text-xs border border-purple-500/30">
                  {targets.length}
                </span>
              </div>

              <div className={`grid gap-6 ${
                targets.length === 1 ? 'grid-cols-1 max-w-xl mx-auto' :
                targets.length === 2 ? 'grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto' :
                'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
              }`}>
                {targets.map((target, targetIdx) => (
                  <div
                    key={targetIdx}
                    className="bg-gradient-to-br from-gray-800/30 to-gray-900/20 rounded-xl border border-gray-600/30 p-5"
                  >
                    {/* Target Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                          <Shield className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                          <h5 className="text-base font-bold text-cyan-300">{target.ipAddress}</h5>
                          <p className="text-xs text-gray-500">{target.tasks.length} outil(s)</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {target.tasks.filter(t => t.status === 'SUCCESS').length > 0 && (
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        )}
                        {target.tasks.filter(t => t.status === 'RUNNING').length > 0 && (
                          <span className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></span>
                        )}
                        {target.tasks.filter(t => t.status === 'FAILED').length > 0 && (
                          <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        )}
                      </div>
                    </div>

                    {/* Tasks Flow */}
                    <div className="space-y-3">
                      {target.tasks.map((task, taskIdx) => (
                        <React.Fragment key={taskIdx}>
                          <div
                            className={`bg-brand-black rounded-lg border p-3 transition-all ${
                              task.status === 'RUNNING' ? 'border-yellow-500/50 shadow-lg shadow-yellow-500/10' :
                              task.status === 'SUCCESS' ? 'border-green-500/30' : 'border-red-500/30'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 rounded-full text-xs font-semibold border border-purple-500/30">
                                {task.toolName}
                              </span>
                              <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(task.status).bg} ${getStatusBadge(task.status).text} ${getStatusBadge(task.status).border}`}>
                                {getStatusIcon(task.status)}
                                <span className="hidden sm:inline">{task.status}</span>
                              </span>
                            </div>
                            <div className="text-xs text-gray-400 grid grid-cols-2 gap-2">
                              <div>
                                <span className="text-gray-500">Debut:</span>
                                <div className="text-gray-300 truncate">{task.startedAt}</div>
                              </div>
                              <div>
                                <span className="text-gray-500">Fin:</span>
                                <div className="text-gray-300 truncate">{task.finishedAt || '-'}</div>
                              </div>
                            </div>
                          </div>

                          {/* Connector */}
                          {taskIdx < target.tasks.length - 1 && (
                            <div className="flex justify-center">
                              <div className="flex flex-col items-center">
                                <div className="w-0.5 h-2 bg-gray-600"></div>
                                <ArrowDown className="w-4 h-4 text-gray-600" />
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="w-full mt-8 pt-6 border-t border-gray-700">
            <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-gray-400">Termine</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                <span className="text-gray-400">En cours</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-gray-400">Failed</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-brand-green" />
                <span className="text-gray-400">AI Decision</span>
              </div>
            </div>
          </div>

          {/* AI Info Box */}
          <div className="w-full max-w-3xl mt-6 mx-auto">
            <div className="bg-brand-green/5 border border-brand-green/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-green/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">🤖</span>
                </div>
                <div className="text-sm">
                  <p className="font-medium text-brand-green mb-1">Orchestration IA Autonome</p>
                  <p className="text-gray-400 text-xs">
                    L'IA analyse les resultats de chaque outil et decide intelligemment du prochain a utiliser.
                    Apres detection d'un port SMB (445), elle choisit automatiquement les outils d'enumeration adaptes.
                  </p>
                  {targets.length > 0 && (
                    <div className="flex items-center gap-4 mt-3 text-xs">
                      <span className="text-brand-green">
                        <strong>{targets.length}</strong> cible(s)
                      </span>
                      <span className="text-purple-400">
                        <strong>{targets.reduce((sum, t) => sum + (t?.tasks?.length || 0), 0)}</strong> outil(s)
                      </span>
                      <span className="text-green-400">
                        <strong>{completedTasks}</strong> termine(s)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PenetrationWorkflowDiagram;
